import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase/config';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import '../styles/Liste.css';
import { formattaData } from '../utils/dateUtils';

// ─────────────────────────────────────────────
// STILI PDF lista risultati
// ─────────────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page: { padding: 20, fontSize: 8, fontFamily: 'Helvetica' },
  intestazione: { flexDirection: 'row', alignItems: 'center', marginBottom: 8, paddingBottom: 6, borderBottom: '1 solid #ccc' },
  logoImg: { width: 60, height: 24, objectFit: 'contain' },
  recapiti: { marginLeft: 10, fontSize: 7, color: '#333' },
  recapitiNome: { fontSize: 8, fontWeight: 'bold', marginBottom: 1 },
  title: { fontSize: 12, fontWeight: 'bold', textAlign: 'center', marginBottom: 8 },
  filtriBox: { backgroundColor: '#e3f2fd', padding: 8, marginBottom: 10, borderRadius: 4 },
  filtriText: { fontSize: 8, textAlign: 'center' },
  divider: { borderBottom: '1 solid #aaa', marginVertical: 4 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#f8f9fa', fontWeight: 'bold', fontSize: 8, borderBottom: '1 solid #dee2e6', minHeight: 20, alignItems: 'center' },
  tableRow: { flexDirection: 'row', borderBottom: '1 solid #dee2e6', minHeight: 18, alignItems: 'center' },
  totaleRow: {
    flexDirection: 'row',
    borderTop: '2 solid #495057',
    minHeight: 20,
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    marginTop: 4,
  },
  totaleLabel: {
    width: '64%',
    padding: 3,
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  totaleValore: {
    width: '10%',
    padding: 3,
    fontSize: 8,
    fontWeight: 'bold',
    color: '#1a6e3c',
  },
  totaleSpacer: {
    width: '26%',
  },
  colData: { width: '10%', padding: 3, fontSize: 7 },
  colDip: { width: '18%', padding: 3, fontSize: 7 },
  colCantiere: { width: '18%', padding: 3, fontSize: 7 },
  colCliente: { width: '18%', padding: 3, fontSize: 7 },
  colOre: { width: '10%', padding: 3, fontSize: 7 },
  colLavori: { width: '26%', padding: 3, fontSize: 7 },
  footer: { position: 'absolute', bottom: 20, left: 30, right: 30, textAlign: 'center', fontSize: 7, color: '#95a5a6' },
  sectionTitle: { fontSize: 8, fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: 2, marginBottom: 2 },
  firmaImg: { width: 160, height: 60, border: '1 solid #999' },
  footerLabel: { fontWeight: 'bold', marginBottom: 1 },
  modificaNote: { fontSize: 7, color: '#e74c3c', marginTop: 6, fontStyle: 'italic' },
});

// ─────────────────────────────────────────────
// PDF LISTA RISULTATI
// ─────────────────────────────────────────────
function ListaGiornalierePDF({ risultati, filtri, dipendentiMap }) {
  function calcTotaleOrePDF() {
    let totMinuti = 0;

    risultati.forEach(g => {
      if (g.assenza || !g.oreTotali) return;

      const str = String(g.oreTotali);
      const matchH = str.match(/(\d+)h/);
      const matchM = str.match(/(\d+)min/);
      const ore = matchH ? parseInt(matchH[1], 10) : 0;
      const minuti = matchM ? parseInt(matchM[1], 10) : 0;
      totMinuti += ore * 60 + minuti;
    });

    const h = Math.floor(totMinuti / 60);
    const m = totMinuti % 60;
    return `${h}h ${String(m).padStart(2, '0')}min`;
  }

  function getFiltriText() {
    const f = [];
    if (filtri.dipendenteId) {
      const d = dipendentiMap[filtri.dipendenteId];
      f.push(`Dipendente: ${d?.nome} ${d?.cognome}`);
    }
    if (filtri.dataInizio) f.push(`Dal: ${formattaData(filtri.dataInizio)}`);
    if (filtri.dataFine) f.push(`Al: ${formattaData(filtri.dataFine)}`);
    return f.length > 0 ? f.join('  |  ') : 'Nessun filtro applicato';
  }

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <View style={pdfStyles.intestazione}>
          <Image src="CRG_logo.png" style={pdfStyles.logoImg} />
          <View style={pdfStyles.recapiti}>
            <Text style={pdfStyles.recapitiNome}>C.R.G. Scavi e trasporti Sagl</Text>
            <Text>Via Livio 5, 6830 Chiasso TI</Text>
          </View>
        </View>
        <Text style={pdfStyles.title}>RICERCA GIORNALIERE</Text>
        <View style={pdfStyles.filtriBox}>
          <Text style={pdfStyles.filtriText}>{getFiltriText()}</Text>
          <Text style={{ ...pdfStyles.filtriText, marginTop: 3 }}>Totale risultati: {risultati.length}</Text>
        </View>
        <View style={pdfStyles.divider} />

        <View style={pdfStyles.tableHeader}>
          <Text style={pdfStyles.colData}>Data</Text>
          <Text style={pdfStyles.colDip}>Dipendente</Text>
          <Text style={pdfStyles.colOre}>Ore lavorate / Stato</Text>
        </View>

        {risultati.map((g, i) => (
          <View key={i} style={pdfStyles.tableRow}>
            <Text style={pdfStyles.colData}>{formattaData(g.data)}</Text>
            <Text style={pdfStyles.colDip}>{g.nomeDipendente || '-'}</Text>
            <Text style={pdfStyles.colOre}>{g.assenza || g.oreTotali || '-'}</Text>
          </View>
        ))}

        <View style={pdfStyles.totaleRow}>
          <Text style={pdfStyles.totaleLabel}>TOTALE ORE LAVORATE EFFETTIVE:</Text>
          <Text style={pdfStyles.totaleValore}>{calcTotaleOrePDF()}</Text>
          <Text style={pdfStyles.totaleSpacer}></Text>
        </View>

        <Text style={pdfStyles.footer}>
          Report generato il {new Date().toLocaleDateString('it-IT')} alle {new Date().toLocaleTimeString('it-IT')} — App Cantieri
        </Text>
      </Page>
    </Document>
  );
}

// ─────────────────────────────────────────────
// PDF SINGOLA GIORNALIERA
// ─────────────────────────────────────────────
function GiornalieraPDF({ dati }) {
  const { nomeDipendente, data, pausaInizio, pausaFine, lavori, oreTotali, modificatoIl, assenza } = dati;
  const lavoriValidi = (lavori || []).filter(l => (l.cantiereLabel || l.lavoroEseguito || l.oraInizio) && l.oraFine);

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.intestazione}>
          <Image src="CRG_logo.png" style={pdfStyles.logoImg} />
          <View style={pdfStyles.recapiti}>
            <Text style={pdfStyles.recapitiNome}>C.R.G. Scavi e trasporti Sagl</Text>
            <Text>Via Livio 5, 6830 Chiasso TI</Text>
            <Text>Tel +41 78 751 32 51 - email: info@crgscavi.ch</Text>
          </View>
        </View>
        <Text style={{ fontSize: 12, fontWeight: 'bold', textAlign: 'center', marginBottom: 8 }}>RAPPORTO GIORNALIERO</Text>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
          <Text style={{ fontSize: 9, fontWeight: 'bold' }}>{nomeDipendente}</Text>
          <Text style={{ fontSize: 9 }}>{formattaData(data)}</Text>
        </View>
        <View style={pdfStyles.divider} />

        {lavoriValidi.map((lavoro, i) => (
          <View key={i}>
            <Text style={pdfStyles.sectionTitle}>Lavoro {i + 1}</Text>
            <View style={{ flexDirection: 'row', marginBottom: 2 }}>
              <Text style={{ fontWeight: 'bold', width: 100 }}>Mezzo:</Text>
              <Text style={{ flex: 1 }}>{lavoro.veicoloLabel || '-'}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 2 }}>
              <Text style={{ fontWeight: 'bold', width: 100 }}>Lavoro eseguito:</Text>
              <Text style={{ flex: 1 }}>{lavoro.lavoroEseguito || '-'}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 2 }}>
              <Text style={{ fontWeight: 'bold', width: 100 }}>Cliente:</Text>
              <Text style={{ flex: 1 }}>{lavoro.clienteLabel || '-'}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 2 }}>
              <Text style={{ fontWeight: 'bold', width: 100 }}>Cantiere:</Text>
              <Text style={{ flex: 1 }}>{lavoro.cantiereLabel || '-'}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 12, marginBottom: 2 }}>
              <View style={{ flexDirection: 'row' }}>
                <Text style={{ fontWeight: 'bold', width: 100 }}>Ora inizio: </Text>
                <Text>{lavoro.oraInizio || '-'}</Text>
              </View>
              <View style={{ flexDirection: 'row' }}>
                <Text style={{ fontWeight: 'bold', width: 100 }}>Ora fine: </Text>
                <Text>{lavoro.oraFine || '-'}</Text>
              </View>
            </View>
            <View style={pdfStyles.divider} />
          </View>
        ))}

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8, paddingTop: 6, borderTop: '1 solid #aaa' }}>
          <View>
            <Text style={pdfStyles.footerLabel}>Pausa pranzo</Text>
            {assenza ? (
              <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{assenza}</Text>
            ) : (
              <Text>Inizio: {pausaInizio || '-'}   Fine: {pausaFine || '-'}</Text>
            )}
          </View>
          <View>
            <Text style={pdfStyles.footerLabel}>Ore totali lavorate</Text>
            <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{oreTotali}</Text>
          </View>
        </View>

        <View style={{ marginTop: 20, borderTop: '1 solid #aaa', paddingTop: 10 }}>
          <Text style={pdfStyles.footerLabel}>Firma dipendente</Text>
          <Text style={{ marginBottom: 6 }}>{nomeDipendente}</Text>
          {dati.firmaUrl
            ? <Image src={dati.firmaUrl} style={pdfStyles.firmaImg} />
            : <Text style={{ color: '#999' }}>Non firmato</Text>
          }
          {modificatoIl && (
            <Text style={pdfStyles.modificaNote}>
              Modificata il: {modificatoIl} — {nomeDipendente}
            </Text>
          )}
        </View>
      </Page>
    </Document>
  );
}

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPALE
// ─────────────────────────────────────────────
function RicercaGiornaliere() {
  const [giornaliere, setGiornaliere] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [filtri, setFiltri] = useState({ dipendenteId: '', dataInizio: '', dataFine: '' });
  const [risultati, setRisultati] = useState([]);
  const [ricercaEffettuata, setRicercaEffettuata] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(null);
  const [generandoLista, setGenerandoLista] = useState(false);

  useEffect(() => { caricaTuttiDati(); }, []);

  async function caricaTuttiDati() {
    const [giorSnap, dipSnap] = await Promise.all([
      get(ref(database, 'giornaliera')),
      get(ref(database, 'dipendenti')),
    ]);
    if (giorSnap.exists()) {
      const tutte = Object.entries(giorSnap.val())
        .map(([k, v]) => ({ id: k, ...v }))
        .filter(g => g.tipo === 'form');
      setGiornaliere(tutte);
    }
    if (dipSnap.exists()) {
      setDipendenti(Object.entries(dipSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    }
  }

  function handleRicerca(e) {
    e.preventDefault();
    let filtrate = [...giornaliere];

    if (filtri.dipendenteId)
      filtrate = filtrate.filter(g => g.dipendenteId === filtri.dipendenteId);
    if (filtri.dataInizio)
      filtrate = filtrate.filter(g => g.data >= filtri.dataInizio);
    if (filtri.dataFine)
      filtrate = filtrate.filter(g => g.data <= filtri.dataFine);

    filtrate.sort((a, b) => b.data.localeCompare(a.data));
    setRisultati(filtrate);
    setRicercaEffettuata(true);
  }

  function resetFiltri() {
    setFiltri({ dipendenteId: '', dataInizio: '', dataFine: '' });
    setRisultati([]);
    setRicercaEffettuata(false);
  }

  function getDescrizioneFiltri() {
    const f = [];
    if (filtri.dipendenteId) {
      const d = dipendenti.find(d => d.id === filtri.dipendenteId);
      f.push(`Dipendente: ${d?.nome} ${d?.cognome}`);
    }
    if (filtri.dataInizio) f.push(`Dal: ${formattaData(filtri.dataInizio)}`);
    if (filtri.dataFine) f.push(`Al: ${formattaData(filtri.dataFine)}`);
    return f.join('  |  ');
  }

  function getBadgeAssenzaStyle(assenza) {
    const isMalattia = assenza === 'MALATTIA';
    return {
      fontSize: 11,
      color: isMalattia ? '#c0392b' : '#b9770e',
      background: isMalattia ? '#fdecea' : '#fff8e1',
      borderRadius: 4,
      padding: '2px 8px',
      fontWeight: 700,
      border: `1px solid ${isMalattia ? '#f5c6cb' : '#f0d58a'}`,
      display: 'inline-block'
    };
  }

  function calcTotaleOreSoloLavorate(lista) {
    let totMinuti = 0;

    lista.forEach(g => {
      if (g.assenza || !g.oreTotali) return;

      const str = String(g.oreTotali);
      const matchH = str.match(/(\d+)h/);
      const matchM = str.match(/(\d+)min/);
      const ore = matchH ? parseInt(matchH[1], 10) : 0;
      const minuti = matchM ? parseInt(matchM[1], 10) : 0;
      totMinuti += ore * 60 + minuti;
    });

    const h = Math.floor(totMinuti / 60);
    const m = totMinuti % 60;
    return `${h}h ${String(m).padStart(2, '0')}min`;
  }

  async function apriPDF(g) {
    setGenerandoPdf(g.id);
    try {
      const blob = await pdf(<GiornalieraPDF dati={g} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('Errore generazione PDF: ' + err.message);
    } finally {
      setGenerandoPdf(null);
    }
  }

  async function scaricaListaPDF() {
    setGenerandoLista(true);
    try {
      const dipendentiMap = dipendenti.reduce((acc, d) => ({ ...acc, [d.id]: d }), {});
      const blob = await pdf(
        <ListaGiornalierePDF risultati={risultati} filtri={filtri} dipendentiMap={dipendentiMap} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ricerca-giornaliere-${filtri.dataInizio || 'tutte'}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setGenerandoLista(false);
    }
  }

  return (
    <div className="lista-container">
      <div className="lista-header">
        <h2>Ricerca Giornaliere</h2>
      </div>

      <div style={{ background: '#f8f9fa', padding: 25, borderRadius: 10, marginBottom: 30 }}>
        <form onSubmit={handleRicerca}>
          <div className="form-ricerca-programmi">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Dipendente</label>
              <select value={filtri.dipendenteId} onChange={e => setFiltri({ ...filtri, dipendenteId: e.target.value })}>
                <option value="">-- Tutti --</option>
                {dipendenti.map(d => (
                  <option key={d.id} value={d.id}>{d.nome} {d.cognome}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Data Inizio</label>
              <input type="date" value={filtri.dataInizio} onChange={e => setFiltri({ ...filtri, dataInizio: e.target.value })} />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Data Fine</label>
              <input type="date" value={filtri.dataFine} onChange={e => setFiltri({ ...filtri, dataFine: e.target.value })} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="submit" className="btn-add" style={{ flex: 1 }}>Cerca</button>
            <button type="button" className="btn-cancel" onClick={resetFiltri} style={{ flex: 1 }}>Reset</button>
          </div>
        </form>
      </div>

      {!ricercaEffettuata ? (
        <div className="empty-state">
          <p>Usa i filtri sopra per cercare le giornaliere.</p>
          <p>Puoi filtrare per dipendente e/o range di date.</p>
        </div>
      ) : risultati.length === 0 ? (
        <div className="empty-state">
          <p>Nessun risultato trovato con i filtri selezionati.</p>
        </div>
      ) : (
        <>
          <div style={{ background: '#efd400', padding: 15, borderRadius: 8, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, border: 'solid 1px #454545' }}>
            <div>
              <strong style={{ fontSize: 16 }}>Trovate {risultati.length} giornaliere</strong>
              {getDescrizioneFiltri() && (
                <p style={{ margin: '5px 0 0 0', fontSize: 13, color: '#333' }}>{getDescrizioneFiltri()}</p>
              )}
            </div>
            <button
              onClick={scaricaListaPDF}
              disabled={generandoLista}
              className="btn-add"
              style={{ background: '#2c3e50', color: '#fff', border: 'none' }}
            >
              {generandoLista ? 'Generazione...' : '📄 Scarica lista PDF'}
            </button>
          </div>

          <div className="desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Dipendente</th>
                  <th>Ore lavorate / Stato</th>
                  <th>Stato modifica</th>
                  <th>Azione</th>
                </tr>
              </thead>
              <tbody>
                {risultati.map(g => (
                  <tr key={g.id}>
                    <td><strong>{formattaData(g.data)}</strong></td>
                    <td>{g.nomeDipendente || '-'}</td>
                    <td>
                      {g.assenza ? (
                        <span style={getBadgeAssenzaStyle(g.assenza)}>
                          {g.assenza}
                        </span>
                      ) : (
                        <strong>{g.oreTotali || '-'}</strong>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {g.modificatoIl && (
                          <span style={{ fontSize: 11, color: '#e67e22', background: '#fef9e7', borderRadius: 4, padding: '2px 6px', fontWeight: 600, border: '1px solid #f0c040' }}>
                            ✏️ Modificata
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => apriPDF(g)}
                        disabled={generandoPdf === g.id}
                        style={{ background: '#2980b9', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                      >
                        {generandoPdf === g.id ? '...' : (
                          <>
                            <img
                              src="/icon-pdf.svg"
                              alt="PDF"
                              style={{ width: 40, verticalAlign: 'middle', marginRight: 6 }}
                            />
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#eaf4fb', borderTop: '2px solid #2980b9' }}>
                  <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, padding: '8px', color: '#2980b9' }}>
                    Totale ore lavorate effettive:
                  </td>
                  <td style={{ fontWeight: 700, fontSize: 15, color: '#2980b9', padding: '8px' }}>
                    {calcTotaleOreSoloLavorate(risultati)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mobile-cards">
            {risultati.map(g => (
              <div key={g.id} className="mobile-card">
                <div className="mobile-card-header" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div>
                    {formattaData(g.data)} — {g.nomeDipendente}
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {g.assenza && (
                      <span style={getBadgeAssenzaStyle(g.assenza)}>
                        {g.assenza}
                      </span>
                    )}

                    {g.modificatoIl && (
                      <span style={{ fontSize: 11, color: '#e67e22', background: '#fef9e7', borderRadius: 4, padding: '2px 6px', fontWeight: 600, border: '1px solid #f0c040' }}>
                        ✏️ Modificata
                      </span>
                    )}
                  </div>
                </div>

                <div className="mobile-card-row">
                  <span className="mobile-card-label">
                    {g.assenza ? 'Stato' : 'Ore totali'}
                  </span>
                  <span className="mobile-card-value">
                    {g.assenza ? (
                      <span style={getBadgeAssenzaStyle(g.assenza)}>{g.assenza}</span>
                    ) : (
                      <strong>{g.oreTotali || '-'}</strong>
                    )}
                  </span>
                </div>

                <div className="mobile-card-actions">
                  <button
                    onClick={() => apriPDF(g)}
                    disabled={generandoPdf === g.id}
                    style={{ background: '#2980b9', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                  >
                    {generandoPdf === g.id ? '...' : '📄 PDF'}
                  </button>
                </div>
              </div>
            ))}

            <div style={{
              background: '#eaf4fb',
              border: '2px solid #2980b9',
              borderRadius: 8,
              padding: '12px 16px',
              marginTop: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: 700, color: '#2980b9', fontSize: 14 }}>
                Totale ore lavorate effettive:
              </span>
              <span style={{ fontWeight: 700, color: '#2980b9', fontSize: 16 }}>
                {calcTotaleOreSoloLavorate(risultati)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default RicercaGiornaliere;
